import React, { useContext } from "react";
import ConditionalRender from "../../../helpers/ConditionalRender";
import { formatNumber } from '../../../helpers/utils';
import Helper from '../../../helpers/Helper';
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import './style.scss';
import Skeleton from "react-loading-skeleton";

interface EventCounts {
    alerts: number;
    changes: number;
    custom: number;
}

interface WidgetCardProps {
    name: string;
    loading?: boolean;
    icon?: string;
    iconClassName?: string;
    value?: string | number | React.ReactNode;
    previous?: number;
    cardTooltipText?: string;
    events?: EventCounts; // New prop for event counts
    type?: 'number' | 'time' | 'percent';
}

function WidgetCard(props: WidgetCardProps) {
    const { 
        name, 
        loading, 
        icon,
        iconClassName,
        value,
        previous,
        cardTooltipText,
        events: eventCounts, 
        type 
    } = props;

    const timeRangeContext = useContext(TimeRangeContext);
    const comparisonSupported = Helper.isComparisonSupported(timeRangeContext);

    // Handle events case
    const hasEvents = !!eventCounts;

    // Handle comparison for direct values
    const getComparison = (current: number, previous: number | undefined) => {
        if (!comparisonSupported || previous === undefined || previous === null || previous === 0) {
            return null;
        }

        if (current === previous) {
            return null;
        }

        const percentageDiff = Helper.getPercentageDifference(current, previous);
        if (percentageDiff === null || !isFinite(percentageDiff)) {
            return null;
        }

        // Format comparison based on type
        let formattedPrevious: string;
        if (type === 'time') {
            formattedPrevious = Helper.getTimeInEnglish(previous);
        } else if (type === 'percent') {
            formattedPrevious = `${formatNumber(previous)}%`;
        } else {
            formattedPrevious = formatNumber(previous);
        }

        return (
            <span className="comparison">
                {current > previous ? (
                    <i className="fal fa-long-arrow-up fa-fw increase" />
                ) : (
                    <i className="fal fa-long-arrow-up fa-fw decrease" />
                )}
                {formattedPrevious}
                <span> ({percentageDiff}%)</span>
            </span>
        );
    };

    // Render events (for event counts case)
    const renderEvents = () => {
        if (!hasEvents || !eventCounts) return null;
        
        return (
            <div>
                <i className="fal fa-exchange fa-fw me-1" data-tip="Total auto-detected change count"></i>
                <span className="me-3">{formatNumber(eventCounts.changes)}</span>
                <i className="fal fa-calendar fa-fw me-1" data-tip="Total custom events count"></i>
                <span className="me-3">{formatNumber(eventCounts.custom)}</span>
                <i className="fal fa-exclamation-triangle red-icon fa-fw me-1" data-tip="Total alerts count"></i>
                <span className="me-3">{formatNumber(eventCounts.alerts)}</span>
            </div>
        );
    };

    // Render the final value with comparison if needed
    const renderValue = () => {
        if (typeof value === 'string' || typeof value === 'number') {
            const currentValue = typeof value === 'string' ? parseFloat(value) : value;
            const comparison = getComparison(currentValue, previous);
            
            // Format value based on type
            let formattedValue: string;
            if (type === 'time') {
                formattedValue = Helper.getTimeInEnglish(currentValue);
            } else if (type === 'percent') {
                formattedValue = `${formatNumber(currentValue)}%`;
            } else {
                formattedValue = formatNumber(currentValue);
            }
            
            return (
                <>
                    {formattedValue}
                    {comparison}
                </>
            );
        }
        
        return value;
    };

    return (
        <div className="col flexlist-item">
            <div className="card">
                <div className="card-body widget">
                    <div className="row g-0 align-items-center card-row">
                        <div 
                            className={`icon ${hasEvents ? 'changes' : iconClassName ?? ''}`} 
                            data-tip={hasEvents ? 'The total event count' : cardTooltipText ?? ''}
                        >
                            <span className={`fal ${hasEvents ? 'fa-exchange' : icon ?? 'fa-exchange'} fa-fw`}></span>
                        </div>
                        <div className="col data">
                            {name}
                            <ConditionalRender if={loading}>
                                <Skeleton />
                            </ConditionalRender>
                            <ConditionalRender if={!loading}>
                                <div>
                                    {hasEvents ? (
                                        renderEvents()
                                    ) : (
                                        <span>
                                            {renderValue()}
                                        </span>
                                    )}
                                </div>
                            </ConditionalRender>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WidgetCard;